import type {
  HubAlgorithmDefinition,
  HubDatasetDefinition,
  HubDeploymentDefinition,
  HubExampleDefinition,
  HubJobDefinition,
} from '@robovision/hub'
import {
  HubDefaultExamplePickingStrategy,
  HubSplitStrategy,
} from '@robovision/hub'


type JobDefinition = HubJobDefinition | HubDeploymentDefinition

const DEFAULT_TRAIN_SPLIT = 0.8
const DEFAULT_TEST_SPLIT = 0.2
const DEFAULT_EXAMPLE_PICKING_STRATEGY = HubDefaultExamplePickingStrategy.LATEST
const DEFAULT_SPLIT_STRATEGY = HubSplitStrategy.STRATIFIED


export interface JobDatasetSplit {
  defaultRatio: number
  id: string
  name?: string
  optional: boolean
  test: boolean
}

export interface JobDatasetDefinitionParams {
  defaultExamplePickingStrategy: HubDefaultExamplePickingStrategy;
  defaultSplitStrategy: HubSplitStrategy;
  description?: string
  exampleDefinition: HubExampleDefinition
  id: string
  manualSplittingEnabled: boolean
  name: string
  splits: JobDatasetSplit[]
  splittingAllowed: boolean
}

export class JobDatasetDefinition {
  // TODO this could be refactored into the `JobDatasetDependency`
  public readonly defaultExamplePickingStrategy: HubDefaultExamplePickingStrategy
  public readonly defaultSplitStrategy: HubSplitStrategy
  public readonly description?: string
  public readonly exampleDefinition: HubExampleDefinition
  public readonly id: string
  public readonly manualSplittingEnabled: boolean
  public readonly name: string
  public readonly splits: JobDatasetSplit[]
  public readonly splittingAllowed: boolean


  private constructor (params: JobDatasetDefinitionParams) {
    this.defaultExamplePickingStrategy = params.defaultExamplePickingStrategy
    this.defaultSplitStrategy = params.defaultSplitStrategy
    this.description = params.description
    this.exampleDefinition = params.exampleDefinition
    this.id = params.id
    this.manualSplittingEnabled = params.manualSplittingEnabled
    this.name = params.name
    this.splits = params.splits
    this.splittingAllowed = params.splittingAllowed
  }

  public static fromDefinition (
    datasetDefinition: HubDatasetDefinition,
    jobDefinition: JobDefinition,
    algorithmDefinition: HubAlgorithmDefinition,
  ) {
    // an example definition is mandatory to have datasets dependencies
    const exampleDefinition = algorithmDefinition.exampleDefinitions
      ?.find(def => def.id === datasetDefinition.exampleDefinition)

    if (!exampleDefinition) {
      throw new Error(`Invalid algorithm definition. Missing example definition ${datasetDefinition.exampleDefinition}`)
    }

    // hints optionally define the default setting for splitting a datapool
    // we try to find a matching one by its example definition id
    const hints = algorithmDefinition.datapoolDefinitions
      ?.find(def => def.exampleDefinition === exampleDefinition.id)
      ?.applicationHints

    // get the dataset dependencies that match the definition
    const datasetDependencies = jobDefinition.dependencies?.datasets
      ?.filter(d => d.datasetDefinition === datasetDefinition.id) ?? []

    // get the count of train and test dependencies to compute the default split ratios
    const countTrain = datasetDependencies.filter(dep => !dep.test).length
    const countTest = datasetDependencies.length - countTrain

    // compute the default split ratios
    const splits = datasetDependencies
      .map<JobDatasetSplit>(dep => {
        // if we have hints use them
        const hint = dep.partDefinitionHint
          ? hints?.defaultSplitSettings?.parts.find(p => p.id === dep.partDefinitionHint)
          : undefined

        // otherwise we'll split uniformly given the default train and test ratios
        const trainSplit = countTest ? DEFAULT_TRAIN_SPLIT : 1
        const defaultRatio = dep.test
          ? DEFAULT_TEST_SPLIT / countTest
          : trainSplit / countTrain

        return {
          defaultRatio: hint?.autoRatio ?? defaultRatio,
          id: dep.id,
          name: dep.name,
          optional: dep.optional ?? false,
          test: !!dep.test,
        }
      }) ?? []

    return new JobDatasetDefinition({
      defaultExamplePickingStrategy: hints?.defaultExamplePickingStrategy ?? DEFAULT_EXAMPLE_PICKING_STRATEGY,
      defaultSplitStrategy: hints?.defaultSplitSettings?.splitStrategy ?? DEFAULT_SPLIT_STRATEGY,
      description: datasetDefinition.description,
      exampleDefinition,
      id: datasetDefinition.id,
      manualSplittingEnabled: hints?.defaultSplitSettings?.manualSplittingEnabled ?? true,
      name: datasetDefinition.name,
      splits,
      splittingAllowed: hints?.splittingAllowed ?? true,
    })
  }
}
